/**
 * Permission parser for NeoTIM
 * Implements client-side permission matching logic
 * 
 * NOTE: The client-side parser is intentionally permissive on scope qualifiers.
 * Its purpose is to determine "could this user potentially have access" for UI rendering.
 * The server always makes the authoritative decision.
 */

import type { PermissionContext } from './types'

/**
 * Parse a permission string into its components
 */
export function parsePermission(permission: string): {
  readonly base: string
  readonly module: string
  readonly resource: string
  readonly action: string
  readonly filialeQualifier: string | null
  readonly scopeQualifier: string | null
} {
  // Handle wildcard
  if (permission === '*') {
    return {
      base: '*',
      module: '*',
      resource: '*',
      action: '*',
      filialeQualifier: null,
      scopeQualifier: null,
    }
  }

  // Split on ':' to separate base from qualifiers
  const colonParts = permission.split(':')
  const base = colonParts[0]

  // Split base on '.' to get module.resource.action
  const dotParts = base.split('.')
  const module = dotParts[0] || ''
  const resource = dotParts[1] || ''
  const action = dotParts[2] || ''

  // Extract qualifiers
  let filialeQualifier: string | null = null
  let scopeQualifier: string | null = null

  for (let i = 1; i < colonParts.length; i += 2) {
    const key = colonParts[i]
    const value = colonParts[i + 1]

    if (key === 'filiale') {
      filialeQualifier = value || null
    } else if (key === 'scope') {
      scopeQualifier = value || null
    }
  }

  return {
    base,
    module,
    resource,
    action,
    filialeQualifier,
    scopeQualifier,
  }
}

/**
 * Check if a required permission is covered by a grant
 * 
 * Rules:
 * - Wildcard '*' covers everything
 * - Domain-level permissions ('rh', 'shared') cover all their module's permissions
 * - Module wildcards ('rh.*') cover all resources in that module
 * - Resource wildcards ('rh.conges.*') cover all actions in that resource
 * - Qualifiers on grant do NOT affect structural matching
 */
export function matchesPermission(
  grant: string,
  required: string,
  _context: PermissionContext
): boolean {
  // Wildcard grant covers everything
  if (grant === '*') {
    return true
  }

  // Parse both permissions
  const grantParsed = parsePermission(grant)
  const requiredParsed = parsePermission(required)

  // Domain-level permission (e.g., 'rh' or 'shared') covers all module permissions
  // If grant is a simple domain with no resource part, it covers all permissions in that domain
  if (grantParsed.module && !grantParsed.resource && !grantParsed.action) {
    // grant is just a domain like 'rh', 'shared', etc.
    // It should match any permission starting with that domain
    return required.startsWith(grantParsed.module + '.')
  }

  // Module must match exactly (no module wildcards, except domain-level above)
  if (grantParsed.module !== requiredParsed.module && grantParsed.module !== '*') {
    return false
  }

  // Resource: grant resource === '*' OR exact match OR empty (domain-level covers all)
  if (grantParsed.resource !== '*' && grantParsed.resource !== '' && grantParsed.resource !== requiredParsed.resource) {
    return false
  }

  // Action: grant action === '*' OR exact match OR empty (resource-level covers all)
  if (grantParsed.action !== '*' && grantParsed.action !== '' && grantParsed.action !== requiredParsed.action) {
    return false
  }

  // Qualifiers on the grant do NOT prevent structural matching
  // (scope checking is the server's responsibility)
  return true
}

/**
 * Check if a permission string is a wildcard (covers all permissions)
 */
export function isWildcard(permission: string): boolean {
  return permission === '*'
}

/**
 * Check if a permission string is a module wildcard (e.g., 'rh.*' or 'rh.*.*')
 */
export function isModuleWildcard(permission: string): boolean {
  const { resource } = parsePermission(permission)
  return resource === '*'
}

/**
 * Check if a permission string is a resource wildcard (e.g., 'rh.conges.*')
 */
export function isResourceWildcard(permission: string): boolean {
  const { action } = parsePermission(permission)
  return action === '*'
}
